import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { TabContent, TabItem, TabNavigation } from '../../components/Tabs';
import { Page, Row } from '../../components';
import ReportUserAccounts from './ReportUserAccounts/ReportUserAccounts';
import ReportUserData from './ReportUserData/ReportUserData';
import ReportUserProgress from './ReportUserProgress/ReportUserProgress';
import ReportsHeader from './ReportHeader';
import dayjs, { Dayjs } from 'dayjs';
import { useLoading } from '../../hooks/useLoading';
import { useService } from '@aesop-fables/containr-react';
import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import {
  SummedReport,
  BreakdownReport,
  OrgReportData as OrgReportType,
} from '../../models/ReportData';
import {
  AdminTierTypeEnum,
  DrillDownWizard,
  drillDownWizardKey,
  tierOrder,
} from '../../services/drillDown';
import { useReportData } from '../../hooks/useReportData';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { getReportCompartmentKeys, reloadReportCompartments } from '../../data/report';
import { Grid, Typography } from '@mui/material';
import { formatTimestamp } from '../../helpers/utilityFunctions';
import { UserCompartments, userStorageKey } from '../../data/user';
import { UserData } from '../../models/UserData';
import { useAdminFilters } from '../../hooks/useAdminHooks';
import { useReportRegeneration } from '../../hooks/useReportRegeneration';
import {
  getLastMonthRange,
  TransactionsDateRange,
} from '../../data/rev-share/helpers/transactionsUtils';
import {
  ReportGenerationService,
  reportGenerationServiceKey,
} from '../../services/reportGeneration';
import Transactions from '../transactions/Transactions';
import DateRangeDropdown from '../transactions/components/DateRangeDropdown';
import { hasProgressData } from '../../data/report/helpers/validationUtils';

const isOrgReportData = (report: BreakdownReport | null): report is OrgReportType => {
  return report !== null && 'createdDate' in report && 'organizationReportId' in report;
};

const Reports: React.FC = () => {
  const { setToast } = useContext(ToastbarContext);
  const { loading, setLoading } = useLoading();
  const appStorage = useAppStorage();

  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const reportGenerationService = useService<ReportGenerationService>(reportGenerationServiceKey);

  const userData = useObservable(userDataCache.observe$<UserData>('user'));
  const tier = useObservable(drillDownWizard.tier$);
  const highestRevenueAccess = useObservable(drillDownWizard.highestRevenueAccess$);
  const regenerating = useObservable(reportGenerationService.regenerating$);
  const compartmentKeys = getReportCompartmentKeys(tier);

  const { checkAdminAccess } = useAdminFilters();
  const { getBreakdownReport, getSummedReport, getEarliestReportDate } = useReportData();
  const { regenerateReport } = useReportRegeneration(tier);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [breakdownReport, setBreakdownReport] = useState<BreakdownReport | null>(null);
  const [summedReport, setSummedReport] = useState<SummedReport | null>(null);
  const [earliestDate, setEarliestDate] = useState<Dayjs | null>(null);
  const [transactionsRange, setTransactionsRange] = useState<TransactionsDateRange>(() =>
    getLastMonthRange(),
  );

  const rootLevel = tier === AdminTierTypeEnum.Root;
  const today = dayjs().endOf('day');
  const isCurrentDate = selectedDate?.isSame(dayjs(), 'day');
  const gcInternalAdmin = checkAdminAccess(userData);

  const showProgress = useMemo(() => {
    return summedReport ? hasProgressData(summedReport) : false;
  }, [summedReport]);
  const showRevenue = useMemo(() => {
    if (!tier || !highestRevenueAccess) return false;

    return tierOrder.indexOf(tier) >= tierOrder.indexOf(highestRevenueAccess);
  }, [highestRevenueAccess, tier]);

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        id: 'userAccounts',
        label: 'User accounts',
        content: (
          <ReportUserAccounts
            summedReport={summedReport}
            breakdownReport={breakdownReport}
            tier={tier}
          />
        ),
      },
      {
        id: 'userData',
        label: 'User data',
        content: <ReportUserData summedReport={summedReport} />,
      },
      {
        id: 'userProgress',
        label: 'User progress',
        visible: showProgress,
        content: <ReportUserProgress summedReport={summedReport} />,
      },
      {
        id: 'transactions',
        label: 'Transactions',
        visible: showRevenue,
        content: <Transactions dateRange={transactionsRange} />,
      },
    ],
    [showProgress, showRevenue, breakdownReport, summedReport, tier, transactionsRange],
  );

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const onRefresh = async () => {
    if (rootLevel) {
      setLoading(true);
    } else {
      setToast(
        new Toast({
          message: `This could take a few minutes. We'll let you know when the refresh is complete.`,
          severity: 'warning',
          open: true,
          autoHideDuration: 6000,
        }),
      );
    }

    try {
      await regenerateReport();
      await fetchReports();

      const successMessage = rootLevel
        ? 'Latest data refreshed!'
        : `${
            tier === AdminTierTypeEnum.Organization ? 'Team' : 'Group'
          } user report refresh is complete. Check out the Reports tab.`;

      setToast(
        new Toast({
          message: successMessage,
          severity: 'success',
          open: true,
          autoHideDuration: 3000,
        }),
      );
    } catch (error: unknown) {
      const timeoutMessage = `${
        tier === AdminTierTypeEnum.Organization ? 'Team' : 'Group'
      } report refresh timed out.`;

      const errorMessage = (error as Error).message;
      console.error(errorMessage);

      setToast(
        new Toast({
          message: errorMessage === 'timeout' ? timeoutMessage : 'Error refreshing report',
          severity: 'error',
          open: true,
          autoHideDuration: 3000,
        }),
      );
    } finally {
      if (rootLevel) {
        setLoading(false);
      }
    }
  };

  const fetchReports = useCallback(async () => {
    if (!selectedDate) return;

    const fetchedBreakdownReport = await getBreakdownReport(selectedDate, tier);
    setBreakdownReport(fetchedBreakdownReport);

    if (tier === AdminTierTypeEnum.Organization || tier === AdminTierTypeEnum.Team) {
      const fetchedSummedReport = await getSummedReport(selectedDate, tier);
      setSummedReport(fetchedSummedReport);
    } else if (tier === AdminTierTypeEnum.Root) {
      setSummedReport(fetchedBreakdownReport as SummedReport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, selectedDate]);

  useEffect(() => {
    const reloadReport = async () => {
      try {
        setLoading(true);
        await reloadReportCompartments(appStorage, compartmentKeys, selectedDate ?? dayjs());
        await fetchReports();
      } catch (error) {
        console.error('Failed to reload the report:', error);
      } finally {
        setLoading(false);
      }
    };
    reloadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReports]);

  useEffect(() => {
    const fetchEarliestDate = async () => {
      const date = await getEarliestReportDate();
      setEarliestDate(date);
    };
    fetchEarliestDate();
  }, [getEarliestReportDate]);

  return (
    <Page>
      <Grid display='flex' flex={1} alignItems='flex-end' mb={2}>
        <Typography variant='p40' color='secondary.main'>
          Reports
        </Typography>
        {(tier === AdminTierTypeEnum.Root || gcInternalAdmin) &&
          isOrgReportData(breakdownReport) &&
          breakdownReport.createdDate &&
          isCurrentDate &&
          tabs[activeTab]?.id !== 'transactions' && (
            <Typography
              display='flex'
              flex={1}
              mb={1}
              justifyContent='flex-end'
              variant='p14'
              color='secondary.main'>
              data last refreshed at {formatTimestamp(breakdownReport.createdDate.toString())}
            </Typography>
          )}
      </Grid>
      <Row style={{ justifyContent: 'space-between' }}>
        <TabNavigation value={activeTab} onChange={handleChange} tabs={tabs} />

        {tabs[activeTab]?.id === 'transactions' && showRevenue ? (
          <DateRangeDropdown
            dateRange={transactionsRange}
            onDateRangeChange={setTransactionsRange}
            today={today}
          />
        ) : (
          <ReportsHeader
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onRefresh={onRefresh}
            earliestDate={earliestDate}
            tier={tier}
            isCurrentDate={isCurrentDate}
            today={today}
            gcInternalAdmin={gcInternalAdmin ?? false}
            loading={loading || regenerating}
          />
        )}
      </Row>
      <TabContent tabs={tabs} value={activeTab} />
    </Page>
  );
};

export default Reports;
